import { someValueIsNull } from '../../../../helpers/arrayHelpers';
import { assertNotNull } from '../../../../helpers/nullHelpers';
import { parseNumberOrNull } from '../../../../helpers/stringHelpers';
import { BuildTypeCode } from '../../../projects/project';
import { RoomFloorConstructionFormModel } from '../GetDataForQuoteOrDesignForm';

export const calculateNumberOfPortsForFloorConstruction = (
  floorConstruction: RoomFloorConstructionFormModel,
  roomAreaInMetresSquared: string,
  projectBuildType: BuildTypeCode,
): number | null => {
  if (floorConstruction.isSkirtingHeating) {
    return 1;
  }

  const numericRoomAreaInMetresSquared = Number(roomAreaInMetresSquared);

  const {
    pipeSpacingInMillimetres,
    maxNumberOfPipesPerPort,
    maxPipeLengthPerPortInMetresForNewBuild,
    maxPipeLengthPerPortInMetresForRetrofit,
  } = floorConstruction;

  const maxPipeLengthPerPortInMetres =
    projectBuildType === 'NewBuild' ? maxPipeLengthPerPortInMetresForNewBuild : maxPipeLengthPerPortInMetresForRetrofit;

  if (
    isNaN(numericRoomAreaInMetresSquared) ||
    pipeSpacingInMillimetres == null ||
    maxPipeLengthPerPortInMetres == null
  ) {
    return null;
  }

  const lengthOfPipeRequiredInMetres = (numericRoomAreaInMetresSquared / pipeSpacingInMillimetres) * 1000;

  const numberOfPorts = lengthOfPipeRequiredInMetres / maxPipeLengthPerPortInMetres / maxNumberOfPipesPerPort;

  return Math.max(1, Math.ceil(numberOfPorts));
};

export type PipesAndPortsCalculationsResult = {
  numberOfPorts: number;
  pipesPerPort: number;
  estimatedLengthOfEachPipeInMetres: number;
};

export const calculatePipesAndPortsForRoom = (formInputs: {
  roomAreaInMetresSquared: string;
  voidAreaInMetresSquared: string;
  unheatedAreaInMetresSquared: string;
  floorConstruction: RoomFloorConstructionFormModel | null;
  projectBuildType: BuildTypeCode;
  manifoldDistanceInMetres: string;
  verticalRunbackInMetres: string;
}): PipesAndPortsCalculationsResult | null => {
  const roomAreaInMetresSquared = parseNumberOrNull(formInputs.roomAreaInMetresSquared);
  const voidAreaInMetresSquared = parseNumberOrNull(formInputs.voidAreaInMetresSquared);
  const unheatedAreaInMetresSquared = parseNumberOrNull(formInputs.unheatedAreaInMetresSquared);
  const floorConstruction = formInputs.floorConstruction;
  const projectBuildType = formInputs.projectBuildType;
  const manifoldDistanceInMetres = parseNumberOrNull(formInputs.manifoldDistanceInMetres);
  const verticalRunbackInMetres = parseNumberOrNull(formInputs.verticalRunbackInMetres);

  if (
    someValueIsNull([
      roomAreaInMetresSquared,
      voidAreaInMetresSquared,
      unheatedAreaInMetresSquared,
      floorConstruction,
      projectBuildType,
      manifoldDistanceInMetres,
      verticalRunbackInMetres,
    ])
  ) {
    return null;
  }

  return calculateNumberOfPipesAndPortsForRoom({
    roomAreaInMetresSquared: assertNotNull(roomAreaInMetresSquared),
    voidAreaInMetresSquared: assertNotNull(voidAreaInMetresSquared),
    unheatedAreaInMetresSquared: assertNotNull(unheatedAreaInMetresSquared),
    floorConstruction: assertNotNull(floorConstruction),
    projectBuildType: assertNotNull(projectBuildType),
    manifoldDistanceInMetres: assertNotNull(manifoldDistanceInMetres),
    verticalRunbackInMetres: assertNotNull(verticalRunbackInMetres),
  });
};

const calculateNumberOfPipesAndPortsForRoom = (inputs: {
  roomAreaInMetresSquared: number;
  voidAreaInMetresSquared: number;
  unheatedAreaInMetresSquared: number;
  floorConstruction: RoomFloorConstructionFormModel;
  projectBuildType: BuildTypeCode;
  manifoldDistanceInMetres: number;
  verticalRunbackInMetres: number;
}): PipesAndPortsCalculationsResult | null => {
  const { floorConstruction } = inputs;

  const { pipeDiameterInMillimetres, pipeSpacingInMillimetres } = floorConstruction;

  if (pipeDiameterInMillimetres == null || pipeSpacingInMillimetres == null) {
    return null;
  }

  const {
    roomAreaInMetresSquared,
    voidAreaInMetresSquared,
    unheatedAreaInMetresSquared,
    manifoldDistanceInMetres,
    verticalRunbackInMetres,
    projectBuildType,
  } = inputs;

  const lengthOfPipeRequiredInRoomInMetres = calculateLengthOfPipeRequiredInRoomInMetres({
    roomAreaInMetresSquared,
    voidAreaInMetresSquared,
    unheatedAreaInMetresSquared,
    pipeSpacingInMillimetres,
  });

  let numberOfPorts = 1;
  let pipesPerPort = 1;

  const maximumCoilLengthInMetres = getMaxCoilLengthInMetresFromPipeDiameterInMillimetres(
    pipeDiameterInMillimetres,
    projectBuildType,
  );

  if (maximumCoilLengthInMetres == null) {
    return null;
  }

  const maxNumberOfPortsPerRoom = 12; // A single Manifold cannot serve more than 12 Ports

  const maxNumberOfPipesPerPort = getMaximumNumberOfPipesPerPortFromPipeDiameterInMillimetres(
    pipeDiameterInMillimetres,
  );

  while (numberOfPorts <= maxNumberOfPortsPerRoom) {
    const estimatedLengthOfEachPipeInMetres = calculateEstimatedLengthOfEachPipeInMetres({
      lengthOfPipeRequiredInRoomInMetres,
      pipesPerPort,
      numberOfPorts,
      manifoldDistanceInMetres,
      verticalRunbackInMetres,
    });

    if (estimatedLengthOfEachPipeInMetres <= maximumCoilLengthInMetres) {
      return { numberOfPorts, pipesPerPort, estimatedLengthOfEachPipeInMetres };
    }

    if (pipesPerPort < maxNumberOfPipesPerPort) {
      pipesPerPort++;
    } else {
      numberOfPorts++;
      pipesPerPort = 1;
    }
  }

  return null;
};

const calculateLengthOfPipeRequiredInRoomInMetres = (inputs: {
  roomAreaInMetresSquared: number;
  voidAreaInMetresSquared: number;
  unheatedAreaInMetresSquared: number;
  pipeSpacingInMillimetres: number;
}) => {
  const {
    roomAreaInMetresSquared,
    voidAreaInMetresSquared,
    unheatedAreaInMetresSquared,
    pipeSpacingInMillimetres,
  } = inputs;

  const usableRoomAreaInMetresSquared =
    roomAreaInMetresSquared - (voidAreaInMetresSquared + unheatedAreaInMetresSquared);

  return usableRoomAreaInMetresSquared * (1000 / pipeSpacingInMillimetres);
};

export const calculateEstimatedLengthOfEachPipeInMetresForRoom = (formInputs: {
  roomAreaInMetresSquared: string;
  voidAreaInMetresSquared: string;
  unheatedAreaInMetresSquared: string;
  floorConstruction: RoomFloorConstructionFormModel | null;
  manifoldDistanceInMetres: string;
  verticalRunbackInMetres: string;
  numberOfPorts: string;
  pipesPerPort: string;
}) => {
  const roomAreaInMetresSquared = parseNumberOrNull(formInputs.roomAreaInMetresSquared);
  const voidAreaInMetresSquared = parseNumberOrNull(formInputs.voidAreaInMetresSquared);
  const unheatedAreaInMetresSquared = parseNumberOrNull(formInputs.unheatedAreaInMetresSquared);
  const pipeSpacingInMillimetres = formInputs.floorConstruction?.pipeSpacingInMillimetres;
  const manifoldDistanceInMetres = parseNumberOrNull(formInputs.manifoldDistanceInMetres);
  const verticalRunbackInMetres = parseNumberOrNull(formInputs.verticalRunbackInMetres);
  const numberOfPorts = parseNumberOrNull(formInputs.numberOfPorts);
  const pipesPerPort = parseNumberOrNull(formInputs.pipesPerPort);

  if (
    someValueIsNull([
      roomAreaInMetresSquared,
      voidAreaInMetresSquared,
      unheatedAreaInMetresSquared,
      pipeSpacingInMillimetres,
      manifoldDistanceInMetres,
      verticalRunbackInMetres,
      numberOfPorts,
      pipesPerPort,
    ])
  ) {
    return null;
  }

  const lengthOfPipeRequiredInRoomInMetres = calculateLengthOfPipeRequiredInRoomInMetres({
    roomAreaInMetresSquared: assertNotNull(roomAreaInMetresSquared),
    voidAreaInMetresSquared: assertNotNull(voidAreaInMetresSquared),
    unheatedAreaInMetresSquared: assertNotNull(unheatedAreaInMetresSquared),
    pipeSpacingInMillimetres: assertNotNull(pipeSpacingInMillimetres),
  });

  return calculateEstimatedLengthOfEachPipeInMetres({
    lengthOfPipeRequiredInRoomInMetres,
    pipesPerPort: assertNotNull(pipesPerPort),
    numberOfPorts: assertNotNull(numberOfPorts),
    manifoldDistanceInMetres: assertNotNull(manifoldDistanceInMetres),
    verticalRunbackInMetres: assertNotNull(verticalRunbackInMetres),
  });
};

export const calculateEstimatedLengthOfEachPipeInMetres = (inputs: {
  lengthOfPipeRequiredInRoomInMetres: number;
  pipesPerPort: number;
  numberOfPorts: number;
  manifoldDistanceInMetres: number;
  verticalRunbackInMetres: number;
}): number => {
  const {
    lengthOfPipeRequiredInRoomInMetres,
    pipesPerPort,
    numberOfPorts,
    manifoldDistanceInMetres,
    verticalRunbackInMetres,
  } = inputs;

  return (
    lengthOfPipeRequiredInRoomInMetres / (pipesPerPort * numberOfPorts) +
    2 * (manifoldDistanceInMetres + verticalRunbackInMetres)
  );
};

const maxCoilLengthInMetresByPipeDiameterInMillimetres: {
  [pipeDiameter in number]: { [projectBuildType in BuildTypeCode]: number };
} = {
  // Pipe Diameter (mm): Max Coil Length (m)
  10: {
    NewBuild: 60,
    Retrofit: 60,
  },
  12: {
    NewBuild: 75,
    Retrofit: 73,
  },
  14: {
    NewBuild: 120,
    Retrofit: 120,
  },
  16: {
    NewBuild: 120,
    Retrofit: 120,
  },
  17: {
    NewBuild: 200,
    Retrofit: 200,
  },
};

const getMaxCoilLengthInMetresFromPipeDiameterInMillimetres = (
  pipeDiameterInMillimetres: number,
  projectBuildType: BuildTypeCode,
): number | null => {
  if (maxCoilLengthInMetresByPipeDiameterInMillimetres[pipeDiameterInMillimetres] == null) {
    return null;
  }

  return maxCoilLengthInMetresByPipeDiameterInMillimetres[pipeDiameterInMillimetres][projectBuildType] || null;
};

const getMaximumNumberOfPipesPerPortFromPipeDiameterInMillimetres = (pipeDiameterInMillimetres: number) =>
  pipeDiameterInMillimetres === 10 ? 4 : 1;
